import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { Check, Loader, Mic, Phone, X, ArrowUp } from 'lucide-react';
import MicWaveForm from "@/components/MicWaveForm";
import useAudioStore from "@/store/useAudioStore";
import { useChatStore } from "@/store/useChatStore";

const showNotification = (message: string, type: 'info' | 'error' = 'info') => {
  if (type === 'error') {
    console.error(`🎤 Speech Recognition: ${message}`);
    alert(`Speech recognition error: ${message}`);
  } else {
    console.log(`🎤 Speech Recognition: ${message}`);
  }
};

interface ChatInputProps {
  autoFocus?: boolean;
  value: string;
  placeholder?: string;
  disabled?: boolean;
  onSend?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onPhoneClick?: () => void;
  showPhoneButton?: boolean;
}

const ChatInput = ({ 
  autoFocus = false,
  value,
  placeholder = "Type a message...", 
  onSend, 
  disabled = false,
  onKeyDown,
  onChange,
  onFocus,
  onPhoneClick,
  showPhoneButton = false,
}: ChatInputProps) => {
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const quest = useChatStore((state) => state.quest);
  const { unlockAudioForIOS } = useAudioStore();

  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 150);
      textarea.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    adjustHeight();
  }, [value]);

  const resetRecording = () => {
    try {
      if (mediaRecorderRef.current) {
        if (mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
        if (mediaRecorderRef.current.stream) {
          mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        }
        mediaRecorderRef.current = null;
      }
      audioChunksRef.current = [];
      setIsListening(false);
      setStream(null);
    } catch (err) {
      console.error('Error during recording reset:', err);
      setIsListening(false);
    }
  };
  
  useEffect(() => {
    const handleBeforeUnload = () => resetRecording();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      resetRecording();
    };
  }, []);

  const getAudioBase64 = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (audioChunksRef.current.length === 0) {
        return reject("No audio recorded");
      }
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      reader.onloadend = () => {
        const base64data = reader.result as string;
        resolve(base64data.split(',')[1]);
      };
      reader.onerror = () => reject("Error converting audio to base64");
    });
  };

  const transcribeAudio = async (): Promise<string> => {
    try {
      setIsProcessing(true);
      const base64Audio = await getAudioBase64();
      const { apiClient } = await import('@/lib/api-client');
      const response = await apiClient.post('/api/transcribe', { audio: base64Audio });
      
      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.details || errorData.error || `Error ${response.status}: Transcription failed`;
        showNotification(errorMessage, 'error');
        return '';
      }
      
      const data = await response.json();
      return data.text || '';
    } catch (error) {
      console.error('Transcription error:', error);
      showNotification('Failed to transcribe audio. Please try again.', 'error');
      return '';
    } finally {
      setIsProcessing(false);
    }
  };

  const startListening = async () => {
    audioChunksRef.current = [];
    setIsListening(true);
    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setStream(audioStream);
      const mediaRecorder = new MediaRecorder(audioStream);
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      });
      mediaRecorder.start(250);
      showNotification('Recording started');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      showNotification('Unable to access microphone. Please check permissions.', 'error');
      setIsListening(false);
    }
  };

  const stopListening = (): Promise<void> => {
    return new Promise((resolve) => {
      if (mediaRecorderRef.current?.state !== 'inactive') {
        mediaRecorderRef?.current?.addEventListener('stop', () => {
          mediaRecorderRef.current?.stream.getTracks().forEach(track => track.stop());
          setIsListening(false);
          setStream(null);
          resolve();
        }, { once: true });
        mediaRecorderRef?.current?.stop();
      } else {
        setIsListening(false);
        setStream(null);
        resolve();
      }
    });
  };

  const handleMicClick = () => {
    useAudioStore.getState().interruptAudio();
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleSendClick = async () => {
    // Unlock audio for iOS on user gesture (button click)
    await unlockAudioForIOS();
    
    if (isListening) {
      await stopListening();
      if (audioChunksRef.current.length > 0) {
        const transcribedText = await transcribeAudio();
        if (transcribedText) {
          onChange({ target: { value: transcribedText } } as React.ChangeEvent<HTMLTextAreaElement>);
        }
      }
    } else if (onSend) {
      onSend();
    }
  };

  const isSendDisabled = disabled || (!value.trim() && !isListening) || isProcessing;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    // 如果内容为空或只有空白字符，允许完全清空
    if (newValue.trim() === '') {
      onChange({ ...e, target: { ...e.target, value: '' } });
      return;
    }
    // 移除开头的换行符
    const trimmedValue = newValue.replace(/^\n+/, '');
    if (trimmedValue !== newValue) {
      onChange({ ...e, target: { ...e.target, value: trimmedValue } });
    } else {
      onChange(e);
    }
  };

  return (
    <div className="flex items-end px-4 py-2 border border-gray-300 rounded-2xl bg-white">
      <div className="relative w-full">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          placeholder={placeholder}
          rows={1}
          disabled={disabled || isListening || isProcessing}
          autoFocus={autoFocus}
          className="w-full text-base bg-transparent resize-none outline-none overflow-y-auto"
          style={{ minHeight: '20px', maxHeight: '150px' }}
        />
        {(isListening || isProcessing) && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80">
            {isListening && stream && <MicWaveForm stream={stream} />}
          </div>
        )}
      </div>
      <div className="flex items-center space-x-1 ml-2">
        {showPhoneButton && (
          <button
            onClick={onPhoneClick}
            disabled={disabled || isProcessing || isListening}
            className="p-2 text-[#7DC146] hover:text-[#6AB238] disabled:opacity-50"
            aria-label="Call Pengi"
            type="button"
          >
            <Phone size={20} />
          </button>
        )}
        <button 
          onClick={handleMicClick}
          disabled={disabled || isProcessing}
          className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
          aria-label={isListening ? "Stop recording" : "Start recording"}
          type="button"
        >
          {isListening ? <X size={20} /> : <Mic size={20} />}
        </button>
        <button
          onClick={handleSendClick}
          disabled={isSendDisabled}
          className="p-2 text-white rounded-full transition-colors"
          style={{ backgroundColor: isSendDisabled ? '#d1d5db' : (quest?.primary_color_theme || '#42C52D') }}
        >
          {isListening ? <Check size={20} /> : isProcessing ? <Loader size={20} className="animate-spin text-white" /> : <ArrowUp className="w-5 h-5 text-white" />}
        </button>
      </div>
    </div>
  );
};

export { ChatInput };
